/**
 * Click-to-zoom on product gallery images (Theme settings > image zoom: "hover").
 *
 * Two defects in the original version made this work on some images and not
 * others:
 *
 * 1. RACE ON CACHED IMAGES. createOverlay() set the overlay image's src and
 *    only afterwards attached its onload handler. A cached image fires `load`
 *    immediately -- before the handler exists -- so the handler never ran and
 *    the overlay was never inserted. The click did nothing at all, silently.
 *    assets/spunge-variant-gallery.js preloads every gallery image (loading =
 *    'eager', img.decode(), plus a warming `new Image()`), so in practice most
 *    images are cached and only the odd uncached one zoomed. The loader's
 *    handlers are now attached BEFORE src, and the already-complete case is
 *    handled explicitly.
 *
 * 2. ONE-SHOT BINDING. enableZoomOnHover() assigned image.onclick across
 *    whatever images existed the moment the script ran. Images that entered
 *    the DOM later -- a variant change re-rendering the gallery, the modal's
 *    innerHTML being replaced -- were never bound. It is now a single
 *    delegated listener on the document, so binding time cannot matter.
 *
 * Also hardened: toggleLoadingSpinner used to dereference the spinner without
 * checking it exists, which throws (and aborts the zoom) for images that have
 * no spinner markup, such as those inside the media modal.
 *
 * enableZoomOnHover(ratio) is kept on window because assets/product-info.js
 * still calls it after a variant swap; it now just updates the zoom ratio.
 */
(function () {
  var zoomRatio = 2;
  var overlay = null;

  function removeOverlay() {
    if (overlay && overlay.parentElement) {
      overlay.parentElement.removeChild(overlay);
    }
    overlay = null;
  }

  function toggleLoadingSpinner(image, show) {
    var opener = image.closest ? image.closest('.product__modal-opener') : null;
    if (!opener && image.parentElement) opener = image.parentElement.parentElement;
    var spinner = opener && opener.querySelector('.loading-overlay__spinner');
    if (!spinner) return; // modal images have no spinner -- this used to throw
    spinner.classList.toggle('hidden', !show);
  }

  // Position the blown-up background so the point under the cursor stays put.
  // Measured off the image itself rather than event.currentTarget: with a
  // delegated listener currentTarget is the document on the first call.
  function moveWithHover(image, event) {
    if (!overlay) return;

    var rect = image.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    var xPercent = ((event.clientX - rect.left) / rect.width) * 100;
    var yPercent = ((event.clientY - rect.top) / rect.height) * 100;

    overlay.style.backgroundPosition = xPercent + '% ' + yPercent + '%';
    overlay.style.backgroundSize = rect.width * zoomRatio + 'px';
  }

  function buildOverlay(src) {
    var el = document.createElement('div');
    el.setAttribute('class', 'image-magnify-full-size');
    el.setAttribute('aria-hidden', 'true');
    el.style.backgroundImage = "url('" + src + "')";
    el.style.backgroundColor = 'var(--gradient-background)';
    return el;
  }

  function magnify(image, event) {
    removeOverlay();

    // currentSrc is the resolution the browser actually picked from srcset;
    // src alone can be a different (often smaller) candidate.
    var src = image.currentSrc || image.src;
    if (!src) return;

    overlay = buildOverlay(src);
    overlay.onclick = removeOverlay;
    overlay.onmouseleave = removeOverlay;
    overlay.onmousemove = function (moveEvent) {
      moveWithHover(image, moveEvent);
    };

    var thisOverlay = overlay;
    var shown = false;

    function show() {
      if (shown) return;
      shown = true;
      toggleLoadingSpinner(image, false);
      image.style.opacity = '100%';
      // Bail if another click already replaced or dismissed this overlay.
      if (overlay !== thisOverlay || !image.parentElement) return;
      image.parentElement.insertBefore(overlay, image);
      moveWithHover(image, event); // sit under the cursor immediately
    }

    image.style.opacity = '50%';
    toggleLoadingSpinner(image, true);

    var loader = new Image();
    loader.onload = show;
    loader.onerror = show; // never leave the image stuck at 50% opacity
    loader.src = src;
    // Handlers are attached before src above, but a cached image may already
    // be complete by now and fire nothing at all -- cover that too.
    if (loader.complete) show();
  }

  document.addEventListener('click', function (event) {
    if (!event.target || !event.target.closest) return;
    var image = event.target.closest('.image-magnify-hover');
    if (!image) return;
    magnify(image, event);
  });

  // Kept for assets/product-info.js, which calls this after a variant change.
  // Delegation means there is nothing left to re-bind; this only sets the ratio.
  window.enableZoomOnHover = function (ratio) {
    if (typeof ratio === 'number' && ratio > 0) zoomRatio = ratio;
  };
})();
